import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface RegistrationFormProps {
  formData: {
    name: string;
    email: string;
    phone: string;
    state: string;
    localGovernment: string;
    yorubaSubgroup: string;
    ageGroup: string;
    gender: string;
    password: string;
    confirmPassword: string;
  };
  handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  nigerianStates: string[];
  availableLGAs: string[];
  yorubaSubgroups: string[];
  setShowFireworks: (show: boolean) => void;
  referrerId: string | null;
}

const ageGroups = ['18-25', '26-35', '36-45', '46-55', '56-65', '65+'];
const genderOptions = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' }
];

const RegistrationForm = ({
  formData,
  handleChange,
  nigerianStates,
  availableLGAs,
  yorubaSubgroups,
  setShowFireworks,
  referrerId
}: RegistrationFormProps) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Custom handler for select elements since the shadcn Select component doesn't work directly with the normal onChange
  const handleSelectChange = (name: string) => (value: string) => {
    // Create a synthetic event-like object
    const customEvent = {
      target: {
        name,
        value
      }
    } as React.ChangeEvent<HTMLSelectElement>;
    handleChange(customEvent);
  };
  
  const validateForm = () => {
    setErrorMessage('');
    
    if (!formData.name || !formData.email || !formData.phone || !formData.state || !formData.localGovernment) {
      setErrorMessage('Please fill in all required fields');
      toast({
        variant: "destructive",
        title: "Registration Error",
        description: "Please fill in all required fields"
      });
      return false;
    }
    
    if (formData.password !== formData.confirmPassword) {
      setErrorMessage('Passwords do not match');
      toast({
        variant: "destructive",
        title: "Registration Error",
        description: "Passwords do not match"
      });
      return false;
    }
    
    if (formData.password.length < 6) {
      setErrorMessage('Password must be at least 6 characters');
      toast({
        variant: "destructive",
        title: "Registration Error", 
        description: "Password must be at least 6 characters"
      });
      return false;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setErrorMessage('Please enter a valid email address');
      toast({
        variant: "destructive",
        title: "Registration Error",
        description: "Please enter a valid email address"
      });
      return false;
    }
    
    return true;
  };
  
  const handleSubmit = async (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    // Reset any previous error messages
    setErrorMessage('');
    
    // Validate the form
    if (!validateForm()) {
      return;
    }
    
    try {
      setIsLoading(true);

      // First, create the user in Supabase Auth
      const {
        data: authData,
        error: authError
      } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          emailRedirectTo: window.location.origin + '/confirmation-success'
        }
      });
      
      if (authError) {
        console.error('Auth signup error:', authError);
        setErrorMessage(authError.message || 'Registration failed. Please try again.');
        toast({
          variant: "destructive",
          title: "Registration Error",
          description: authError.message || 'Registration failed. Please try again.'
        });
        return;
      }
      
      if (!authData.user) {
        const errorMsg = "User registration failed. Please try again later.";
        setErrorMessage(errorMsg);
        toast({
          variant: "destructive",
          title: "Registration Error",
          description: errorMsg
        });
        return;
      }

      // Prefetch the referrer's name if there's a referrerId
      let referrerName = null;
      if (referrerId) {
        try {
          const {
            data: referrerData,
            error: referrerError
          } = await supabase.from('community_members').select('nickname, full_name').eq('id', referrerId).single();
          if (!referrerError && referrerData) {
            referrerName = referrerData.nickname || referrerData.full_name;
          }
        } catch (error) {
          console.error('Error fetching referrer info:', error);
          // Non-critical error, continue with registration
        }
      }

      // Then create a record in the community_members table
      const {
        error: profileError
      } = await supabase.from('community_members').insert({
        id: authData.user.id,
        full_name: formData.name,
        email: formData.email,
        phone_number: formData.phone,
        state: formData.state,
        local_government: formData.localGovernment,
        sub_group: formData.yorubaSubgroup || null,
        age_group: formData.ageGroup || null,
        gender: formData.gender || null,
        referred_by: referrerId || null,
        referrer_name: referrerName
      });
      
      if (profileError) {
        console.error('Profile creation error:', profileError);
        setErrorMessage(profileError.message || 'Failed to create member profile. Please contact support.');
        toast({
          variant: "destructive",
          title: "Registration Error",
          description: profileError.message || 'Failed to create member profile. Please contact support.'
        });
        return;
      }

      // Register was successful
      setShowFireworks(true);
      toast({
        title: "Registration Successful",
        description: "Please wait for admin approval."
      });

      // Navigate to community page which will show the pending approval message
      setTimeout(() => {
        navigate('/community');
      }, 2500);
    } catch (error) {
      console.error('Registration error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Registration failed. Please try again.';
      setErrorMessage(errorMessage);
      toast({
        variant: "destructive",
        title: "Registration Error",
        description: errorMessage
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {errorMessage && (
        <div className="p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
          {errorMessage}
        </div>
      )}
      
      <div>
        <Label htmlFor="name" className="text-gray-900 font-medium">Full Name</Label>
        <Input 
          id="name" 
          name="name" 
          value={formData.name} 
          onChange={handleChange} 
          placeholder="Enter your full name" 
          required 
          className="text-gray-900 bg-white border-gray-300 focus:border-omoduduwa-terracotta placeholder:text-gray-500"
        />
      </div>

      <div>
        <Label htmlFor="email" className="text-gray-900 font-medium">Email Address</Label>
        <Input 
          id="email" 
          name="email" 
          type="email" 
          value={formData.email} 
          onChange={handleChange} 
          placeholder="you@example.com" 
          required 
          className="text-gray-900 bg-white border-gray-300 focus:border-omoduduwa-terracotta placeholder:text-gray-500"
        />
      </div>

      <div>
        <Label htmlFor="phone" className="text-gray-900 font-medium">Phone Number</Label>
        <Input 
          id="phone" 
          name="phone" 
          value={formData.phone} 
          onChange={handleChange} 
          placeholder="Enter your phone number" 
          required 
          className="text-gray-900 bg-white border-gray-300 focus:border-omoduduwa-terracotta placeholder:text-gray-500"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="state" className="text-gray-900 font-medium">State of Residence</Label>
          <Select value={formData.state} onValueChange={handleSelectChange('state')} disabled={isLoading}>
            <SelectTrigger className="text-gray-900 bg-white border-gray-300 focus:border-omoduduwa-terracotta">
              <SelectValue placeholder="Select State" className="text-gray-900" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              {nigerianStates.map(state => <SelectItem key={state} value={state} className="text-gray-900">{state}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="localGovernment" className="text-gray-900 font-medium">LGA of Residence</Label>
          <Select value={formData.localGovernment} onValueChange={handleSelectChange('localGovernment')} disabled={!formData.state || isLoading}>
            <SelectTrigger className="text-gray-900 bg-white border-gray-300 focus:border-omoduduwa-terracotta">
              <SelectValue placeholder="Select LGA" className="text-gray-900" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              {availableLGAs.map(lga => <SelectItem key={lga} value={lga} className="text-gray-900">{lga}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="ageGroup" className="text-gray-900 font-medium">Age Group</Label>
          <Select value={formData.ageGroup} onValueChange={handleSelectChange('ageGroup')} disabled={isLoading}>
            <SelectTrigger className="text-gray-900 bg-white border-gray-300 focus:border-omoduduwa-terracotta">
              <SelectValue placeholder="Select Age Group" className="text-gray-900" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              {ageGroups.map(group => <SelectItem key={group} value={group} className="text-gray-900">{group}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="gender" className="text-gray-900 font-medium">Gender</Label>
          <Select value={formData.gender} onValueChange={handleSelectChange('gender')} disabled={isLoading}>
            <SelectTrigger className="text-gray-900 bg-white border-gray-300 focus:border-omoduduwa-terracotta">
              <SelectValue placeholder="Select Gender" className="text-gray-900" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              {genderOptions.map(option => <SelectItem key={option.value} value={option.value} className="text-gray-900">{option.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label htmlFor="yorubaSubgroup" className="text-gray-900 font-medium">Yoruba Sub-group (Optional)</Label>
        <Select value={formData.yorubaSubgroup} onValueChange={handleSelectChange('yorubaSubgroup')} disabled={isLoading}>
          <SelectTrigger className="text-gray-900 bg-white border-gray-300 focus:border-omoduduwa-terracotta">
            <SelectValue placeholder="Select Sub-group" className="text-gray-900" />
          </SelectTrigger>
          <SelectContent className="bg-white">
            {yorubaSubgroups.map(group => <SelectItem key={group} value={group} className="text-gray-900">{group}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label htmlFor="password" className="text-gray-900 font-medium">Password</Label>
        <Input 
          id="password" 
          name="password" 
          type="password" 
          value={formData.password} 
          onChange={handleChange} 
          placeholder="Create a secure password" 
          required 
          className="text-gray-900 bg-white border-gray-300 focus:border-omoduduwa-terracotta placeholder:text-gray-500"
        />
        <p className="text-xs text-gray-700 mt-1">Password must be at least 6 characters long</p>
      </div>

      <div>
        <Label htmlFor="confirmPassword" className="text-gray-900 font-medium">Confirm Password</Label>
        <Input 
          id="confirmPassword" 
          name="confirmPassword" 
          type="password" 
          value={formData.confirmPassword} 
          onChange={handleChange} 
          placeholder="Confirm your password" 
          required 
          className="text-gray-900 bg-white border-gray-300 focus:border-omoduduwa-terracotta placeholder:text-gray-500"
        />
      </div>

      {referrerId && (
        <div className="p-3 bg-green-50 border border-green-200 rounded text-green-800 text-sm">
          You were invited by someone in our community. You'll appear in their referrals after registration.
        </div>
      )}

      <Button type="submit" className="w-full bg-omoduduwa-terracotta hover:bg-omoduduwa-terracotta/90 text-white" disabled={isLoading}>
        {isLoading ? (
          <>
            <span className="mr-2 inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
            Registering...
          </>
        ) : 'Register'}
      </Button>
    </form>
  );
};

export default RegistrationForm;
